import React from 'react';
import { useDocumentTheme } from './DocumentThemeScope';
import { config } from 'src/config';

// Read only the company attached to the document, never the signed-in workspace.
export const clientDocumentCompany = (document: any) =>
  document?.companyOwnership?.company ??
  document?.Event?.companyOwnership?.company ??
  document?.company ?? null;

export const clientDocumentBranding = (document: any) => {
  const company = clientDocumentCompany(document);
  const color = (value: unknown, fallback: string) =>
    typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
  const filename = typeof (company?.Logo ?? company?.logo) === 'string' ? (company.Logo ?? company.logo) : '';
  const logoUrl = filename
    ? /^https?:\/\//i.test(filename)
      ? filename
      : `${String(config.FILe_URL ?? '').replace(/\/$/, '')}/${filename.replace(/^\//, '')}`
    : null;
  const primaryColor = color(company?.primaryColor, '#000000');
  const secondaryColor = color(company?.secondaryColor, '#E8BF21');
  const textColor = (background: string) => {
    const channels = [1, 3, 5].map((offset) => {
      const value = parseInt(background.slice(offset, offset + 2), 16) / 255;
      return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    });
    const luminance = channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
    return luminance > 0.179 ? '#000000' : '#ffffff';
  };
  return {
    primaryTextColor: textColor(primaryColor),
    secondaryTextColor: textColor(secondaryColor),
    name: company?.name || '',
    address: company?.address || '',
    phone: company?.phoneNumber || '',
    email: company?.email || '',
    logoUrl,
    primaryColor,
    secondaryColor
  };
};

export const ClientDocumentLogo = ({ branding, height = 90, width = 90 }: {
  branding: ReturnType<typeof clientDocumentBranding>;
  height?: number;
  width?: number;
}) => {
  const theme = useDocumentTheme();
  if (theme && !theme.documentHeader.showLogo) return null;
  const logoUrl = theme?.documentHeader.logoData || branding.logoUrl;
  if (theme) height = width = ({ small: 48, medium: 72, large: 100 }[theme.documentHeader.logoSize] ?? 72);
  const bannerLogoSize = ({ small: 160, medium: 192, large: 224 }[theme?.documentHeader.logoSize] ?? 192);
  return logoUrl ? (
  <img data-client-document-logo src={logoUrl} alt={branding.name} height={height} width={width}
    crossOrigin="anonymous" style={{ objectFit: 'contain', '--document-logo-size': bannerLogoSize + 'px' } as React.CSSProperties} />
) : (
  <span data-client-document-logo style={{ display: 'block', textAlign: 'center', whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: '18px', fontSize: 12,
    height, width, maxWidth: width, color: branding.secondaryColor, fontWeight: 700 }}>
    {branding.name}
  </span>
);
};

export const DocumentCompanyHeader = ({ company }: {company: any}) => {
  const theme = useDocumentTheme();
  if (!theme) return null;
  const branding = clientDocumentBranding({company});
  return <><ClientDocumentLogo branding={branding}/><div data-document-company-details>
    <strong>{branding.name}</strong><p>{branding.address}</p><p>{branding.phone}</p><p>{branding.email}</p>
  </div></>;
};
